import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbRouterLink,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { PATHROUTES } from '@/constants/path'
import { cn } from '@/lib/utils'
import SponsorshipSummaryDialog from './SponsorshipSummaryDialog'
import { useQuery } from '@tanstack/react-query'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { getSponsorShipPlans, getSponsorEventDetails } from '@/api/sponsorApi'
import type {
  SponsorShipPlan,
  SponsorshipDetailsType,
} from '@/schemas/sponsor.schems'
import CustomLoader from '../CustomLoader'
import { CheckOutlineIcon, CloseIcon } from '../Icons'
import { Badge } from '../ui/badge'
import { createMarkup, getPackageBenifits } from '@/constants/event'
import { Check } from 'lucide-react'
import { formatPriceWithCommas } from '@/utils/formFieldUtils'

const LIMIT = 100
const PAGE = 1

function SelectPackageData() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [eventName, setEventName] = useState('First Tracks Morning Session')
  const [selectedTier, setSelectedTier] = useState<string>('')
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false)

  const {
    data: sponsorShipDetailsData,
    isLoading: isLoadingSponsorShipDetails,
  } = useQuery({
    queryKey: SPONSOR_QUERY_KEYS.byId(id as string),
    queryFn: () => getSponsorEventDetails(id as string),
    enabled: !!id,
  })

  const sponsorShipDetails: SponsorshipDetailsType | undefined =
    sponsorShipDetailsData?.data

  const { data: sponsorShipPlanData, isLoading: isSponsorShipPlanLoading } =
    useQuery({
      queryKey: SPONSOR_QUERY_KEYS.sponsorShipPlansById(
        sponsorShipDetails?.event?.id ?? ''
      ),
      queryFn: () => {
        const params = sponsorShipDetails?.subEvent
          ? {
              limit: LIMIT,
              page: PAGE,
              subEventId: sponsorShipDetails?.subEvent.id ?? '',
              eventId: sponsorShipDetails?.event?.id ?? '',
            }
          : {
              limit: LIMIT,
              page: PAGE,
              eventId: sponsorShipDetails?.event?.id ?? '',
            }
        return getSponsorShipPlans(params)
      },
      enabled: !!sponsorShipDetails && !!sponsorShipDetails?.event?.id,
    })

  const sponsorShipPlans: SponsorShipPlan[] = sponsorShipPlanData?.data?.result

  useEffect(() => {
    if (sponsorShipDetails) {
      if (sponsorShipDetails.event?.name) {
        setEventName(
          sponsorShipDetails?.subEvent
            ? sponsorShipDetails.subEvent.name
            : sponsorShipDetails.event.name
        )
      }

      if (sponsorShipDetails.plan?.id) {
        setSelectedTier(sponsorShipDetails.plan.id)
      }
    }
  }, [sponsorShipDetails])

  const handleCancel = () => {
    navigate(PATHROUTES.DASHBOARD.SPONSORSHIP_MANAGEMENT)
  }

  const handleSaveAndContinue = () => {
    // Open the summary dialog with selected data
    setIsSummaryDialogOpen(true)
  }

  const canProceed = selectedTier

  // Helper functions to get selected data for the dialog
  const getSelectedTierData = () => {
    return sponsorShipPlans?.find(tier => tier.id === selectedTier)
  }

  const handleTierSelection = (tierId: string, soldOut: boolean) => {
    if (!soldOut) {
      setSelectedTier(tierId)
    }
  }

  const handleKeyDown = (
    event: React.KeyboardEvent,
    tierId: string,
    soldOut: boolean
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleTierSelection(tierId, soldOut)
    }
  }

  if ((id && isLoadingSponsorShipDetails) || isSponsorShipPlanLoading) {
    return <CustomLoader />
  }

  return (
    <div className="flex bg-gradient-to-br from-blue-50/40 to-blue-100/20">
      {/* Sidebar would go here in full layout - matching existing layout */}
      <main className="flex-1 p-4">
        {/* Page Header */}
        <div className="p-4 pb-2.5 border-b border-white/20 mb-6">
          {/* Breadcrumb Navigation */}
          <Breadcrumb className="mb-2.5">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbRouterLink
                  to={PATHROUTES.DASHBOARD.SPONSORSHIP_MANAGEMENT}
                  className="text-ss-neutral-600 text-sm font-semibold"
                >
                  My Sponsorships
                </BreadcrumbRouterLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbRouterLink
                  to={
                    id
                      ? PATHROUTES.DASHBOARD.SPONSORSHIP_DETAILS_WITH_ID(id)
                      : PATHROUTES.DASHBOARD.SPONSORSHIP_DETAILS
                  }
                  className="text-ss-neutral-600 text-sm font-semibold"
                >
                  Sponsorship Detail
                </BreadcrumbRouterLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-ss-blue-500 text-sm font-semibold">
                  {sponsorShipDetails?.plan ? 'Edit Package' : 'Select Package'}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Page Title and Description */}
          <div className="space-y-0.5">
            <h1 className="text-2xl text-ss-neutral-950 leading-8 font-semibold">
              {sponsorShipDetails?.plan
                ? 'Edit Sponsorship Package'
                : 'Select Sponsorship Package'}
            </h1>
            <p className="text-sm text-ss-neutral-600 leading-4.5  font-normal">
              {sponsorShipDetails?.plan
                ? `Update your sponsorship tier and ad placements for your ${sponsorShipDetails?.subEvent ? 'sub event' : 'event'}.`
                : `Choose your sponsorship tier and ad placements for your ${sponsorShipDetails?.subEvent ? 'sub event' : 'event'}.`}
            </p>
          </div>
        </div>
        <div className="bg-white/[0.78] border border-white rounded-2xl">
          {/* Form Content */}
          <div className="flex flex-col min-h-[calc(100vh-200px)]">
            <div className="flex-1 p-4 space-y-6">
              {/* Event Name Field */}
              <div className="space-y-1">
                <Label
                  htmlFor="event-name"
                  className="text-sm font-medium text-ss-neutral-950"
                >
                  {sponsorShipDetails?.subEvent
                    ? 'Sub Event Name'
                    : 'Event Name'}
                </Label>
                <p
                  id="event-name"
                  className="bg-ss-neutral-25 border text-base border-ss-neutral-50 text-ss-neutral-700 h-11 rounded-lg px-3 py-1 flex items-center"
                  aria-describedby="event-name-desc"
                >
                  {eventName}
                </p>
              </div>

              {/* Sponsorship Packages */}
              <div className="space-y-3">
                <h2 className="text-base font-bold text-ss-neutral-950 leading-[22px]">
                  Sponsorship Packages
                </h2>

                <RadioGroup
                  value={selectedTier}
                  onValueChange={setSelectedTier}
                  className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                >
                  {sponsorShipPlans && sponsorShipPlans?.length > 0 ? (
                    sponsorShipPlans?.map(tier => {
                      return (
                        <div
                          key={tier.id}
                          role="button"
                          tabIndex={tier.soldOut ? -1 : 0}
                          className={cn(
                            'bg-white border border-ss-neutral-50 rounded-xl p-4 space-y-4 transition-all inset-shadow-lg md:min-w-xs',
                            !tier.soldOut &&
                              'cursor-pointer focus:ring-2 focus:ring-ss-blue-500 focus:ring-offset-2 focus:outline-none'
                          )}
                          onClick={() =>
                            handleTierSelection(tier.id, tier.soldOut)
                          }
                          onKeyDown={e =>
                            handleKeyDown(e, tier.id, tier.soldOut)
                          }
                          aria-label={`Select ${tier.name} sponsorship package for $${tier.price.toLocaleString()}${tier.soldOut ? ' - Sold out' : ''}`}
                          aria-disabled={tier.soldOut}
                        >
                          <div className="flex flex-col gap-3">
                            <div className="flex items-start gap-3">
                              <RadioGroupItem
                                value={tier.id}
                                id={tier.id}
                                className={cn(
                                  'mt-1 transition-all',
                                  selectedTier === tier.id
                                    ? 'border-ss-blue-500 bg-ss-blue-500 text-white'
                                    : 'border-ss-neutral-200 bg-white hover:border-ss-blue-300'
                                )}
                                disabled={tier.soldOut}
                              />
                              <div className="flex-1 min-w-0">
                                <Label
                                  htmlFor={tier.id}
                                  className={cn(
                                    'text-xl font-bold text-ss-neutral-950 cursor-pointer leading-7 wrap-anywhere block'
                                  )}
                                >
                                  {tier.name}
                                </Label>
                              </div>
                            </div>

                            <div className="flex flex-col items-start justify-between gap-3 pl-6 md:flex-row">
                              <span className="text-2xl font-bold text-ss-neutral-950 leading-none tracking-tight">
                                ${formatPriceWithCommas(tier.price)}
                              </span>

                              {tier.soldOut ? (
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    'px-3 py-1.5 text-xs font-semibold rounded-full',
                                    'bg-ss-warning-50 text-ss-warning-200 border-ss-warning-200',
                                    'flex items-center gap-1.5'
                                  )}
                                >
                                  <CloseIcon />
                                  <span>
                                    {tier.availability.sold}/
                                    {tier.availability.allowed} Sold Out
                                  </span>
                                </Badge>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    'px-3 py-1.5 text-xs font-semibold rounded-full',
                                    'bg-ss-positive-50 text-ss-positive-2000 border-ss-positive-200',
                                    'flex items-center gap-1.5'
                                  )}
                                >
                                  <Check />
                                  <span>
                                    {tier.availability.available}/
                                    {tier.availability.allowed} Available
                                  </span>
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div
                            className={cn(
                              'w-full h-0.5 bg-[repeating-linear-gradient(to_right,#d1d5db_0_8px,transparent_8px_16px)] border-ss-neutral-50 my-3 lg:my-6 shrink-0'
                            )}
                          />
                          <div className=" pt-4">
                            <h3 className="text-base font-semibold text-ss-neutral-950 mb-3.5 leading-[22px]">
                              Key Benefits
                            </h3>
                            <ul className="space-y-4">
                              {getPackageBenifits(tier?.description).map(
                                (benefit, index) => (
                                  <li
                                    key={index}
                                    className="flex items-center gap-3"
                                  >
                                    <CheckOutlineIcon
                                      className="w-6 h-6 text-ss-positive-200 flex-shrink-0 mt-0.5"
                                      aria-hidden="true"
                                    />
                                    <span
                                      className="text-base text-ss-neutral-600 leading-[22px] wrap-anywhere"
                                      dangerouslySetInnerHTML={createMarkup(
                                        benefit
                                      )}
                                    ></span>
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div className="col-span-full flex flex-col items-center justify-center py-16 px-4">
                      <div className="bg-white border border-ss-neutral-50 rounded-2xl p-8 max-w-md w-full text-center space-y-4">
                        {/* Icon */}
                        <div className="mx-auto w-16 h-16 bg-ss-neutral-50 rounded-full flex items-center justify-center">
                          <svg
                            className="w-8 h-8 text-ss-neutral-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                            />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-ss-neutral-950">
                          No Packages Available
                        </h3>
                        <p className="text-sm text-ss-neutral-600 leading-relaxed">
                          There are currently no sponsorship packages available
                          for this event.
                        </p>
                      </div>
                    </div>
                  )}
                </RadioGroup>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="bg-white border-t border-ss-neutral-50 p-6 rounded-b-2xl shadow-[0px_5px_40px_0px_rgba(0,0,0,0.1)]">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="h-9 px-4 border-ss-neutral-200 text-ss-neutral-950 hover:bg-ss-neutral-50 font-semibold"
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveAndContinue} disabled={!canProceed}>
                  Save & Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Sponsorship Summary Dialog */}
      <SponsorshipSummaryDialog
        open={isSummaryDialogOpen}
        onOpenChange={setIsSummaryDialogOpen}
        selectedTier={getSelectedTierData()}
        eventId={sponsorShipDetails?.event?.id}
        sponsorShipId={id}
      />
    </div>
  )
}

export default SelectPackageData
